import api from './api';

export interface WorkbenchTool {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  category: string;
  icon: string | null;
  tool_type: string;
  docker_image: string | null;
  is_active: boolean;
  is_favorite: boolean;
}

export interface NmapScanResult {
  target: string;
  scan_type: string;
  output: string;
  success: boolean;
}

export interface NmapScanOptions {
  target: string;
  scan_type: string;
  scan_technique: string;
  timing: number;
  ports?: string;
  aggressive: boolean;
  service_version: boolean;
  os_detection: boolean;
  default_scripts: boolean;
  traceroute: boolean;
  no_ping: boolean;
  verbose: boolean;
}

export interface SubfinderScanResult {
  domain: string;
  subdomains: string[];
  count: number;
  success: boolean;
}

export interface TestsslScanResult {
  target: string;
  checks: string;
  output: string;
  success: boolean;
}

// ─── Draw.io Diagrams ─────────────────────────────────────────

export interface DrawioDiagramListItem {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface DrawioDiagramDetail {
  id: number;
  name: string;
  xml: string;
  created_at: string;
  updated_at: string;
}

export const drawioService = {
  listDiagrams: async (): Promise<DrawioDiagramListItem[]> => {
    const res = await api.get('/workbench/drawio/diagrams');
    return res.data;
  },

  getDiagram: async (id: number): Promise<DrawioDiagramDetail> => {
    const res = await api.get(`/workbench/drawio/diagrams/${id}`);
    return res.data;
  },

  createDiagram: async (name: string): Promise<DrawioDiagramDetail> => {
    const res = await api.post('/workbench/drawio/diagrams', { name });
    return res.data;
  },

  updateDiagram: async (id: number, data: { name?: string; xml?: string }): Promise<DrawioDiagramDetail> => {
    const res = await api.put(`/workbench/drawio/diagrams/${id}`, data);
    return res.data;
  },

  deleteDiagram: async (id: number): Promise<void> => {
    await api.delete(`/workbench/drawio/diagrams/${id}`);
  },
};

export const workbenchService = {
  getTools: async (): Promise<WorkbenchTool[]> => {
    const res = await api.get('/workbench/tools');
    return res.data;
  },

  getFavorites: async (): Promise<WorkbenchTool[]> => {
    const res = await api.get('/workbench/favorites');
    return res.data;
  },

  addFavorite: async (toolId: number): Promise<void> => {
    await api.post(`/workbench/favorites/${toolId}`);
  },

  removeFavorite: async (toolId: number): Promise<void> => {
    await api.delete(`/workbench/favorites/${toolId}`);
  },

  nmapScan: async (options: NmapScanOptions): Promise<NmapScanResult> => {
    const res = await api.post('/workbench/nmap/scan', {
      ...options,
      ports: options.ports || null,
    }, { timeout: 310_000 });
    return res.data;
  },

  subfinderScan: async (domain: string): Promise<SubfinderScanResult> => {
    const res = await api.post('/workbench/subfinder/scan', {
      domain,
    }, { timeout: 130_000 });
    return res.data;
  },

  testsslScan: async (target: string, checks: string): Promise<TestsslScanResult> => {
    const res = await api.post('/workbench/testssl/scan', {
      target,
      checks,
    }, { timeout: 310_000 });
    return res.data;
  },
};
